import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import { type GraphQLTaggedNode } from 'react-relay';
import {
	createOperationDescriptor,
	type ConcreteRequest,
	getRequest,
	type RecordSourceProxy,
	type ReaderSelection,
} from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import {
	syncQueryCacheBridge,
	type QueryCacheBridgeSource,
} from './cacheBridge/queryCacheBridgeSync.ts';
import { recordOptimisticQueryWriteRollback } from './optimisticInterop/optimisticQueryWriteRollback.ts';
import { readQueryInterop } from './readQuery.ts';
import { writeQueryFields } from './relayQueryWriteOperations.ts';

export type WriteQueryInteropArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<DataProxy.WriteQueryOptions<TData, TVariables>, 'data' | 'variables'> & {
	query: GraphQLTaggedNode;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function writeQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): void {
	const source: QueryCacheBridgeSource = isRelayEnabled(params.__callerPackage__)
		? 'relay'
		: 'apollo';

	recordOptimisticQueryWriteRollback({
		query: params.query,
		variables: params.variables,
		callerPackage: params.__callerPackage__,
		readPreviousData: () =>
			readQueryInterop<TData, TVariables>({
				query: params.query,
				variables: params.variables,
				__callerPackage__: params.__callerPackage__,
			}),
		writePreviousData: (data) =>
			writeQueryInterop<TData, TVariables>({
				...params,
				data,
			}),
		writeData: () => writeQueryInterop<TData, TVariables>(params),
	});

	const didWrite =
		source === 'relay'
			? writeQueryInteropRelay<TData, TVariables>(params)
			: writeQueryInteropApollo<TData, TVariables>(params);

	if (didWrite) {
		syncQueryCacheBridge({
			source,
			query: params.query,
			variables: params.variables,
			data: params.data,
		});
	}
}

function writeQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: WriteQueryInteropArgs<TData, TVariables>,
): boolean {
	// Guard window for SSR; this only runs client-side.
	const relayEnv = typeof window !== 'undefined' ? window.GLOBAL_RELAY_ENV : undefined;
	const { query, data, variables } = params;

	if (!relayEnv || !data) {
		return false;
	}

	let didWrite = false;
	relayEnv.commitUpdate((storeProxy: RecordSourceProxy) => {
		const root = storeProxy.getRoot();
		if (!root) {
			return;
		}

		const queryNode = query as ConcreteRequest;
		const queryFields = queryNode?.operation?.selections;

		if (queryFields) {
			didWrite = writeQueryFields(
				root,
				queryFields as readonly ReaderSelection[],
				data,
				variables || {},
			);
		}
	});

	if (!didWrite) {
		// If no existing Relay records were updated, fall back to Relay's normal payload
		// write path. Invalid Relay artifacts should still throw here, preserving the
		// original writeQueryInterop failure behavior.
		const operation = createOperationDescriptor(getRequest(query), variables ?? {});
		relayEnv.commitPayload(operation, data as Record<string, unknown>);
		didWrite = true;
	}

	return didWrite;
}

function writeQueryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: WriteQueryInteropArgs<TData, TVariables>): boolean {
	window['GLOBAL_APOLLO_CLIENT']!.writeQuery({
		...params,
		query: toDocumentNode(params.query),
	});
	return true;
}
